import { Injectable } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { ExportJob } from './model/export-job';
import { ExportJobStatus } from './model/export-job-status';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import { get, pick, set } from 'lodash';
import { finalize, take } from 'rxjs/operators';

import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  User,
  IdTokenResult,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  onSnapshot,
  query,
  where,
  Firestore,
} from 'firebase/firestore';

// import firestore from 'firebase/firestore'
// import QueryDocumentSnapshot = firebase.firestore.QueryDocumentSnapshot;
// import WhereFilterOp = firebase.firestore.WhereFilterOp;
// import DocRef = firebase.firestore.DocumentReference;
import { IMakeRequestOptions, IRequestOptions } from '../interface';
// import IdTokenResult = firebase.auth.IdTokenResult;
// Configs
import { environment } from '../../../environments/environment';
import {
  IFirestoreCondition,
  IFirestoreMethods,
  TFirestoreRef,
  IFirestoreListenPathStore,
} from '../interface';
import { MetricsHttpService } from '../http/metrics-http.service';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  public exportsUseCase: IFirestoreMethods;
  public trackedExports = new BehaviorSubject<Array<ExportJob>>([]);
  private readonly firestoreKeys: Array<string> = [
    'apiKey',
    'appId',
    'authDomain',
    'databaseURL',
    'measurementId',
    'messagingSenderId',
    'projectId',
    'storageBucket',
  ];
  private readonly firestoreKeysRequired: Array<string> = [
    'apiKey',
    'appId',
    'authDomain',
    'databaseURL',
    'messagingSenderId',
    'projectId',
    'storageBucket',
  ];
  private readonly signOutTimeout: number = 1000;
  private readonly firestoreResetMarginSeconds: number = 120;
  private client: { [useCase: string]: FirebaseApp } = {};
  private databases: { [useCase: string]: Firestore } = {};
  private identityTokens: { [useCase: string]: string } = {};
  private refreshTokens: { [useCase: string]: string } = {};
  private refreshFns: { [useCase: string]: () => Promise<string> } = {};
  private refreshTimers: { [useCase: string]: any } = {};
  private connected: { [useCase: string]: boolean } = {};
  private disconnectSubjects: { [useCase: string]: Subject<void> } = {};
  private listenPaths: IFirestoreListenPathStore = {};

  public get statMap(): { [docStatus: string]: string } {
    return {
      Working: 'Processing',
      Queued: 'Processing',
      Success: 'Done',
      Failure: 'Fail',
    };
  }

  constructor(
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private metricsHttp: MetricsHttpService
  ) {
    if ('firestore' in environment) {
      Object.keys(environment.firestore).forEach((useCase: string) => {
        const config = environment.firestore[useCase];
        // Check if API key is a placeholder and skip initialization if so
        if (
          config.apiKey &&
          config.apiKey.startsWith('@') &&
          config.apiKey.endsWith('@')
        ) {
          console.warn(
            `Firestore ${useCase}: API key is a placeholder (${config.apiKey}). Skipping initialization.`
          );
          return;
        }
        this.initializeDb(useCase, config);
      });
      // Only start the use case if it was successfully initialized
      if (this.isInitialized('exports')) {
        this.startUseCase('exports');
      }
    }
  }
  /**
   * Used to start tracking a given export based on its path within firestore.
   * @param path
   */
  public trackExport(path: string): void {
    // Check if Firestore is initialized before attempting to track
    if (!this.isInitialized('exports')) {
      console.warn(
        'Firestore exports use case is not initialized. Cannot track export.'
      );
      return;
    }

    if ('exports' in this.identityTokens) {
      this.exportsUseCase
        .subscribeToPath(path)
        .pipe(debounceTime(500))
        .subscribe((data) => {
          if (data && data[0] && data[0].length > 0) {
            const jobData = data[0][0]; // First element of data array, first item
            const jobId = data[1][0]; // First element of id array
            const job: ExportJob = {
              creation: jobData.creation,
              file: jobData.file,
              notify: true,
              state_id: jobData.state_id,
              status: ExportJobStatus[jobData.status],
              user_id: jobData.user_id,
              user_email: jobData.user_email,
              expires: 0,
              store_id: '',
              id: jobId,
            };
            this.onJobChanged(job);
          }
        });
    } else {
      this.getToken((data) => {
        const token = JSON.parse(data).fbt;
        if (!token) {
          return;
        }

        this.signInWithToken(token)
          .then(() => {
            this.exportsUseCase
              .subscribeToPath(path)
              .pipe(debounceTime(500))
              .subscribe((data) => {
                if (data && data[0] && data[0].length > 0) {
                  const jobData = data[0][0]; // First element of data array, first item
                  const jobId = data[1][0]; // First element of id array
                  const job: ExportJob = {
                    creation: jobData.creation,
                    file: jobData.file,
                    notify: true,
                    state_id: jobData.state_id,
                    status: ExportJobStatus[jobData.status],
                    user_id: jobData.user_id,
                    user_email: jobData.user_email,
                    expires: 0,
                    store_id: '',
                    id: jobId,
                  };
                  this.onJobChanged(job);
                }
              });
          })
          .catch((err) => {
            throw err;
          });
      });
    }
  }

  private getToken(cb): void {
    const url = `${environment.metricsApiUrl}/getFirestoreToken`;
    const options = { requestOptions: this.readyOptions() };
    this.makeRequest('post', url, options).subscribe((data) => cb(data));
  }

  private makeRequest(
    method: 'post' | 'get',
    url: string,
    options: IMakeRequestOptions = {}
  ): Observable<any> {
    options.requestOptions = this.readyOptions(options.requestOptions);
    return this.metricsHttp.post('getFirestoreToken', options);
  }

  private readyOptions(requestOptions: IRequestOptions = {}): IRequestOptions {
    if (!('observe' in requestOptions)) {
      requestOptions.observe = 'body';
    }

    set(
      requestOptions,
      'responseType',
      get(requestOptions, 'responseType', 'json')
    );
    set(
      requestOptions,
      'headers.Content-Type',
      get(
        requestOptions,
        'headers.Content-Type',
        'application/json; charset=utf-8'
      )
    );

    const bearerToken = get(this, 'auth.token');
    if (bearerToken) {
      set(requestOptions, 'headers.authorization', 'Bearer ' + bearerToken);
    }
    return requestOptions;
  }

  /**
   * Executed whenever the status of an export job changes in firestore.
   * @param job
   */
  private onJobChanged(job: ExportJob): void {
    this.showToast(job);
  }

  /**
   * Shows a localized toast based on the status of the current job.
   * @param job
   */
  private showToast(job: ExportJob) {
    const jobStatus = job.status.toLocaleLowerCase();
    // let toastType = job.status === ExportJobStatus.Failure ? 'error' : 'info';
    let message: string = this.translateService.instant(
      `main.tabs.downloads.toasts.messages.${jobStatus}`
    );
    if (!message) {
      message = this.translateService.instant(
        `main.tabs.downloads.toasts.messages.default`
      );
    }
    message = message.replace(`{file}`, job.file);
    if (job.status === ExportJobStatus.Failure) {
      this.toasterService.error(
        this.translateService.instant(
          `main.tabs.downloads.toasts.titles.${jobStatus}`
        ),
        message
      );
    } else if (job.status === ExportJobStatus.Success) {
      this.toasterService.success(
        this.translateService.instant(
          `main.tabs.downloads.toasts.titles.${jobStatus}`
        ),
        message
      );
    } else {
      this.toasterService.info(
        this.translateService.instant(
          `main.tabs.downloads.toasts.titles.${jobStatus}`
        ),
        message
      );
    }
  }
  isInitialized(useCase: string): boolean {
    return useCase in this.client;
  }

  isConnected(useCase: string): boolean {
    return this.isInitialized(useCase) && get(this.connected, useCase, false);
  }

  initializeDb(useCase: string, config: any): void {
    if (this.isInitialized(useCase)) {
      return;
    }
    this.client[useCase] = initializeApp(config, useCase);
    this.databases[useCase] = getFirestore(this.client[useCase]);
  }

  startUseCase(useCase: string) {
    if (!(useCase in this.client)) {
      console.warn(
        `Firestore use case '${useCase}' is not initialized. Skipping startUseCase.`
      );
      return;
    } else if (!this.isInitialized(useCase)) {
      console.warn(
        `Firestore use case '${useCase}' is not initialized. Skipping startUseCase.`
      );
      return;
    }

    set(this.disconnectSubjects, useCase, new Subject());

    this.exportsUseCase = {
      getPath: (path: string, conditions: Array<IFirestoreCondition>) =>
        this.getPath(useCase, path, conditions),
      subscribeToPath: (
        path: string,
        conditions?: Array<IFirestoreCondition>
      ) => this.subscribeToPath(useCase, path, conditions),
      signIn: (
        token,
        callback?: (err: null | Error, success: boolean) => void
      ) => this.signIn(useCase, token, callback),
      signOut: (callback?: (err: null | Error, success?: boolean) => void) =>
        this.signOut(useCase, callback),
      isConnected: (): boolean => this.isConnected(useCase),
      disconnect$: () =>
        this.disconnectSubjects[useCase].asObservable().pipe(take(1)),
    };
  }

  signOutOfAll(): Promise<void> {
    const disconnects: Array<Promise<void>> = [];

    Object.keys(this.client)
      .filter((useCase) => this.isConnected(useCase))
      .forEach((useCase) =>
        disconnects.push(
          new Promise((resolve, reject) => {
            // eslint-disable-next-line prefer-const
            let timer: any;
            const resolved = () => {
              clearTimeout(timer);
              resolve();
            };
            timer = setTimeout(() => {
              resolve();
            }, this.signOutTimeout);
            this.signOut(useCase, (err) => (err ? reject(err) : resolved()));
          })
        )
      );

    const promiseChain = Promise.resolve();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    disconnects.forEach((promise) => promiseChain.then(() => promise));
    return promiseChain;
  }

  private validateConfig(config: any): boolean {
    return !this.firestoreKeysRequired.filter((key) => !(key in config)).length;
  }

  private signIn(
    useCase: string,
    token: string,
    callback?: (err: null | Error, success: boolean) => void
  ): void {
    const auth = getAuth(this.client[useCase]);
    signInWithCustomToken(auth, token)
      .then((credentials) => {
        onAuthStateChanged(auth, (user: User) =>
          this.handleAuthChange(useCase, user)
        );
        if (callback) {
          callback(null, true);
        }
      })
      .catch((err) => {
        if (callback) {
          callback(err, false);
        }
      });
  }

  private signInWithToken(token: string): Promise<void> {
    return new Promise((resolve, reject) =>
      this.exportsUseCase.signIn(
        token,
        (err: Error | null, success: boolean) => {
          if (err) {
            return reject(err);
          }
          resolve();
        }
      )
    );
  }

  private handleAuthChange(useCase: string, user: User): Promise<void> {
    if (!!user && 'refreshToken' in user) {
      this.refreshTokens[useCase] = get(user, 'refreshToken');
    } else if (!user) {
      return;
    }

    // Store our 'get new token' function
    this.refreshFns[useCase] = () => user.getIdToken(true);

    return (
      user
        .getIdTokenResult()
        // Get expiration time of token
        .then((meta: IdTokenResult) => {
          const expireTime = new Date(meta.expirationTime).getTime();
          const nowTime = new Date().getTime();
          return Math.floor((expireTime - nowTime) / 1000);
        })
        // Check the tokens and ensure they're valid and have enough time
        .then(async (secondsLeft: number) =>
          this.checkExpiration(useCase, secondsLeft)
        )
        // Setup the refresh timer
        .then((secondsLeft: number) => {
          this.refreshTimers[useCase] = setTimeout(() => {
            this.refreshToken(useCase).catch((reason) => {
              if (
                reason.message.startsWith(
                  'The custom token format is incorrect.'
                )
              ) {
                return;
              }
            });
          }, secondsLeft * 1000);
        })
    );
  }

  private async checkExpiration(
    useCase: string,
    secondsLeft: number
  ): Promise<number> {
    if (secondsLeft <= 0) {
      // -- Token already expired.
      secondsLeft = await this.refreshToken(useCase)
        .then(() => {
          // Reset the time remaining (59m 55s)
          return 3595;
        })
        .catch((reason) => {
          return 0;
        });
    } else if (secondsLeft <= this.firestoreResetMarginSeconds) {
      // -- Reset the token now
      secondsLeft = await this.refreshToken(useCase)
        .then(() => {
          // Reset the time remaining (59m 55s)
          return 3595;
        })
        .catch((err) => {
          return 0;
        });
    }

    return secondsLeft > 5 ? secondsLeft - 5 : secondsLeft;
  }

  private refreshToken(useCase: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.refreshFns[useCase]().then((freshIdToken: string) => {
        this.signIn(useCase, freshIdToken, (err, success) => {
          if (err) {
            return reject(err);
          } else if (success) {
            this.identityTokens[useCase] = freshIdToken;
          }
          resolve();
        });
      });
    });
  }

  private signOut(
    useCase: string,
    callback?: (err: null | Error, success?: boolean) => void
  ): void {
    if (!(useCase in this.identityTokens) || !this.identityTokens[useCase]) {
      return;
    }

    const auth = getAuth(this.client[useCase]);

    signOut(auth)
      .then(() => {
        // End all listeners
        const keys = Object.keys(this.listenPaths[useCase]);
        if (keys.length) {
          keys.forEach((path: string) => this.killListener(useCase, path));
        }

        // Unset the database
        this.resetFirestoreSession(useCase);
        if (callback) {
          callback(null, true);
        }
      })
      .catch((err) => {
        if (callback) {
          callback(err);
        }
      });
  }

  private setupFirestoreSession(useCase: string, token: string): void {
    this.identityTokens[useCase] = token;
    this.connected[useCase] = true;
  }

  private resetFirestoreSession(useCase: string): void {
    this.databases[useCase] = undefined;
    this.connected[useCase] = false;
    this.identityTokens[useCase] = '';
  }

  /**
   * Sets or unsets monitoring on a specific FS path
   */
  private subscribeToPath(
    useCase: string,
    path: string,
    conditions?: Array<any>
  ): Observable<[any, any]> {
    const firestore = this.databases[useCase];
    const pathSegments = path.split('/').filter((s) => s.length > 0);
    const isDocument = pathSegments.length % 2 === 0; // Even number = document

    let ref: any;

    if (isDocument) {
      // It's a document reference
      ref = doc(firestore, path);
      return new Observable((subject) => {
        const unsubscribe = onSnapshot(ref, (snapshot: any) => {
          if (snapshot.exists()) {
            subject.next([[snapshot.data()], [snapshot.id]]);
          } else {
            subject.next([[], []]);
          }
        });
        return unsubscribe;
      });
    } else {
      // It's a collection reference
      if (conditions) {
        ref = query(
          collection(firestore, path),
          ...conditions.map((cond) => where(cond.prop, cond.op, cond.compare))
        );
      } else {
        ref = collection(firestore, path);
      }
      return new Observable((subject) => {
        const unsubscribe = onSnapshot(ref, (snapshot: any) => {
          subject.next([
            snapshot.docs.map((doc) => doc.data()),
            snapshot.docs.map((doc) => doc.id),
          ]);
        });
        return unsubscribe;
      });
    }
  }

  private killListener(useCase: string, path: string): void {
    const listener = get(this.listenPaths, useCase + '.' + path);
    if (listener === undefined) {
      return;
    }

    if (listener.subject) {
      try {
        (<any>listener.subject).complete();
      } catch (e) {}
    }

    if (
      !!listener.unsubscriber &&
      typeof listener.unsubscriber === 'function'
    ) {
      // Kill the listener
      (<any>listener).unsubscriber();
    }
  }

  private getListener(
    useCase: string,
    path: string,
    conditions?: Array<IFirestoreCondition>
  ): [Subject<any>, string, [TFirestoreRef?, boolean?]] {
    let ref;
    let type: string;
    let subject: Subject<any>;

    // We're not getting the path here in case the path string has a `.` (period) in it.
    let existingListener = get(this.listenPaths, useCase);
    if (existingListener) {
      existingListener =
        path in existingListener ? <any>existingListener[path] : undefined;
    }
    if (!!existingListener && !(<any>existingListener.subject).isStopped) {
      ref = existingListener.ref[0];
      type = existingListener.ref[1] ? 'doc' : 'collection';
      subject = <any>existingListener.subject;
    } else {
      // If the subject was stopped, we'll want to start over.
      if (existingListener) {
        this.killListener(useCase, path);
      }

      // Set the listener
      ref = this.getPath(useCase, path, conditions);
      if (ref.length) {
        // Create a new observable (behavior)subject
        subject = new Subject();
        subject.pipe(finalize(() => this.killListener(useCase, path)));
        type = ref[1] ? 'doc' : 'collection';
      }
    }

    return [subject, type, ref];
  }

  /**
   * Returns a collection/doc reference obj
   */
  private getPath(
    useCase: string,
    path: string,
    conditions?: Array<IFirestoreCondition>
  ): [TFirestoreRef?, boolean?] {
    if (!(useCase in this.connected) || !this.connected[useCase]) {
      // this.debug.debug('(' + useCase + '):getPath() FS instance not active.')
      return [];
    }

    let isDoc = true;
    let ref = null;
    try {
      path.split('/').every((pathItem, i) => {
        if (i === 0) {
          // ref = this.databases[useCase].collection(pathItem);
          ref = collection(this.databases[useCase], pathItem);
        } else if (ref === undefined) {
          return false;
        } else if (isDoc) {
          ref = ref.doc(pathItem);
          isDoc = false;
        } else {
          if (pathItem.indexOf(':') !== -1) {
            const piS = pathItem.split(':');
            pathItem = piS[0];
            const whereParts = piS[1].split('|', 3);
            ref
              .collection(pathItem)
              .where(whereParts[0], whereParts[1], whereParts[2]);
          } else {
            ref = ref.collection(pathItem);
          }
          isDoc = true;
        }
        return ref !== undefined;
      });
    } catch (e) {
      return [];
    }

    if (conditions) {
      conditions.forEach((condition: IFirestoreCondition) => {
        switch (condition.type) {
          case 'where':
            ref = ref.where(condition.prop, condition.op, condition.compare);
            break;
        }
      });
    }

    return [ref, !isDoc];
  }
}
